// src/pages/Dashboard/Userpage.js
// ==================== Imports ====================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Nav, Spinner } from 'react-bootstrap';
import RequestModal from './requestmodel'; // Import the separated RequestModal
import ProductAdd from './Productadd'; // <-- Reuse your Add modal for Edit
import CONFIG from '../../config';
import { apiFetch } from '../../utils/api';

// ==================== Helper: API origin + getImageUrl ====================
/**
 * CONFIG.API_URL may be something like:
 *  - https://demo.swapiify.com
 *  - https://demo.swapiify.com/
 *  - https://demo.swapiify.com/api
 *  - https://demo.swapiify.com/api/
 *
 * We want the site root (no trailing slash, no '/api' suffix) so uploads path becomes:
 *   `${siteRoot}/uploads/products/...`
 */
const RAW_API_URL = (CONFIG.API_URL || '').replace(/\/+$/, '');
const SITE_ROOT = RAW_API_URL.replace(/\/api$/i, ''); // remove trailing '/api' if present

/**
 * Accepts either a product object or a string path and returns an absolute URL or null.
 * - If input is a product object with product.image or product.image_full_url, uses that.
 * - If input is a string, uses that as image path or full URL.
 * - Returns null for empty/invalid values (so <img src={null} won't be rendered).
 */
function getImageUrl(input) {
  if (!input) return null;

  // If it's an object, try common fields
  if (typeof input === 'object') {
    const possible =
      input.image_full_url ||
      input.image_full ||
      input.image_url ||
      input.main_image ||
      input.image ||
      null;
    return getImageUrl(possible);
  }

  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // If absolute URL already, return as-is
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // If starts with a slash, treat as absolute path on site root
  if (trimmed.startsWith('/')) {
    return `${SITE_ROOT}${trimmed}`;
  }

  // If path already starts with "uploads/" (common), prefix with site root
  if (/^uploads\//i.test(trimmed)) {
    return `${SITE_ROOT}/${trimmed}`;
  }

  // If path contains "uploads/" anywhere (like "api/uploads/..." or "uploads/..."), normalize:
  const uploadsIndex = trimmed.toLowerCase().indexOf('uploads/');
  if (uploadsIndex !== -1) {
    const after = trimmed.slice(uploadsIndex); // "uploads/..."
    return `${SITE_ROOT}/${after}`;
  }

  // Otherwise treat as relative: prefix with site root
  return `${SITE_ROOT}/${trimmed}`;
}

// ------------------ Helper: Ensure timestamp and sorting ------------------
function ensureTimestamp(prod) {
  if (!prod) return prod;
  // prefer common server timestamp fields, else fallback to Date.now()
  const ts =
    prod.created_at ||
    prod.createdAt ||
    prod.createdAtTimestamp ||
    prod._created_at ||
    prod.created ||
    null;
  return { ...prod, created_at: ts || Date.now() };
}

function sortNewestFirst(a, b) {
  const ta = Number(new Date(a?.created_at || a?.createdAt || 0)) || 0;
  const tb = Number(new Date(b?.created_at || b?.createdAt || 0)) || 0;
  return tb - ta; // descend: newest first
}

// ---------------- STATUS HELPERS ----------------
function getStatusLabel(status) {
  const s = (status || '').toString().toLowerCase();
  if (s === 'accepted') return 'Approved for Swap';
  if (s === 'pending') return 'Pending Approval';
  if (s === 'rejected') return 'Rejected';
  return 'Status Unknown';
}

function getStatusClass(status) {
  const s = (status || '').toString().toLowerCase();
  if (s === 'accepted') return 'approved';
  if (s === 'pending') return 'pending';
  if (s === 'rejected') return 'rejected';
  return 'unknown';
}

// ---------------- STAR RATING COMPONENT ----------------
const StarRating = ({ rating }) => {
  const totalStars = 5;
  const numericRating = parseFloat(rating) || 0;

  const fullStar = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="url(#starGradient)"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="starGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#FFD700', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#FFA500', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" />
    </svg>
  );

  const halfStar = (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
        fill="#E0E0E0"
      />
      <path
        d="M12 17.27V2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
        fill="url(#starGradient)"
      />
    </svg>
  );

  const emptyStar = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="#E0E0E0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" />
    </svg>
  );

  const stars = [];
  for (let i = 1; i <= totalStars; i++) {
    if (i <= numericRating) stars.push(<span key={`star-${i}`}>{fullStar}</span>);
    else if (i - 0.5 <= numericRating) stars.push(<span key={`star-${i}`}>{halfStar}</span>);
    else stars.push(<span key={`star-${i}`}>{emptyStar}</span>);
  }
  return <div className="star-rating-display">{stars}</div>;
};

// ---------------- PRODUCT CARD COMPONENT ----------------
const ProductCard = ({ product, index, onProductClick }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const preloaderRef = useRef(null);

  const statusLabel = getStatusLabel(product?.approval_status);

  useEffect(() => {
    let cancelled = false;
    setImageLoaded(false);

    const resolved = getImageUrl(product) || null;

    // Cleanup any previous preloader
    if (preloaderRef.current) {
      preloaderRef.current.onload = null;
      preloaderRef.current.onerror = null;
      preloaderRef.current = null;
    }

    if (!resolved) {
      // No image -> show default block after a tiny delay so UI doesn't jump
      setImgSrc(null);
      setImageLoaded(true);
      return;
    }

    const img = new Image();
    preloaderRef.current = img;

    img.onload = () => {
      if (!cancelled) {
        setImgSrc(resolved);
        setImageLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        // fallback to a bundled default image path (relative to your public folder)
        setImgSrc('/images/default-product.jpg');
        setImageLoaded(true);
        console.warn(`ProductCard: failed to load ${resolved}`);
      }
    };

    // start loading
    img.src = resolved;

    return () => {
      cancelled = true;
      if (preloaderRef.current) {
        preloaderRef.current.onload = null;
        preloaderRef.current.onerror = null;
        preloaderRef.current = null;
      }
    };
  }, [product]);

  return (
    <Col
      xs={12}
      sm={6}
      md={4}
      lg={3}
      className="mb-4 product-item-animate"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div
        className="card explore-box card-animate"
        style={{ cursor: 'pointer', ...styles.productCard }}
        onClick={() => onProductClick(product)}
      >
        <div className="explore-place-bid-img">
          {!imageLoaded && (
            <div style={styles.imageLoading}>
              <Spinner animation="border" size="sm" />
              <span>Loading...</span>
            </div>
          )}
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name || 'Product'}
              className="card-img-top explore-img"
              style={{
                ...styles.productImage,
                opacity: imageLoaded ? 1 : 0
              }}
              loading="lazy"
            />
          ) : (
            <div
              style={{
                ...styles.productImage,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6c757d'
              }}
            >
              No Image
            </div>
          )}
          <div className="bg-overlay"></div>
          <div className="place-bid-btn">
            <span className="btn btn-success">
              <i className="ri-eye-fill align-bottom me-1"></i> View Details
            </span>
          </div>
        </div>
        <div className="card-body">
          <p className="fw-medium mb-0 float-end">
            <i className="mdi mdi-heart text-danger align-middle"></i> {product.rating || ''}
          </p>
          <h5 className="mb-1 text-truncate">{product.name}</h5>
          <p className="text-muted mb-0">{product.category || 'Uncategorized'}</p>
        </div>
        <div className="card-footer border-top border-top-dashed">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 fs-14">
              <i className="ri-price-tag-3-fill text-warning align-bottom me-1"></i>
              {statusLabel}
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};

// ---------------- PREMIUM MODAL ----------------
const PremiumModal = ({ isOpen, onClose, product, onEdit }) => {
  const navigate = useNavigate();
  const [productImg, setProductImg] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const preloaderRef = useRef(null);

  const statusLabel = getStatusLabel(product?.approval_status);
  const statusClass = getStatusClass(product?.approval_status);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ==================== Handle Image Loading ====================
  useEffect(() => {
    if (!product) {
      setProductImg(null);
      setImageLoaded(true);
      return;
    }
    setImageLoaded(false);

    const resolved = getImageUrl(product) || null;

    // Cleanup previous preloader if present
    if (preloaderRef.current) {
      preloaderRef.current.onload = null;
      preloaderRef.current.onerror = null;
      preloaderRef.current = null;
    }

    if (!resolved) {
      setProductImg(null);
      setImageLoaded(true);
      return;
    }

    const img = new Image();
    preloaderRef.current = img;

    let cancelled = false;
    img.onload = () => {
      if (!cancelled) {
        setProductImg(resolved);
        setImageLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setProductImg('/images/default-product.jpg');
        setImageLoaded(true);
        console.warn(`PremiumModal: failed to load ${resolved}`);
      }
    };
    img.src = resolved;

    return () => {
      cancelled = true;
      if (preloaderRef.current) {
        preloaderRef.current.onload = null;
        preloaderRef.current.onerror = null;
        preloaderRef.current = null;
      }
    };
  }, [product]);

  const handleNavigate = () => {
    if (!product) return;
    navigate(`/product/${product.id}`);
    onClose(); // This closes the modal
  };

  if (!isOpen || !product) return null;

  return (
    <div className="premium-modal-overlay" onClick={onClose}>
      <div className="premium-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="premium-modal-close-btn" onClick={onClose} title="Close">
          &times;
        </button>
        <div className="premium-modal-body-grid">
          <div className="premium-modal-img-container">
            {!imageLoaded && productImg && (
              <div style={styles.modalImageLoading}>
                <Spinner animation="border" />
                <span>Loading image...</span>
              </div>
            )}
            {productImg ? (
              <img
                src={productImg}
                alt={product.name || 'Product'}
                className="premium-modal-img"
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8f9fa'
                }}
              >
                No Image Available
              </div>
            )}
          </div>
          <div className="premium-modal-details">
            <p className="premium-modal-category">{product.category}</p>
            <h2 className="premium-modal-title">{product.name}</h2>
            <p className="premium-modal-description">
              {product.description || 'No detailed description is available for this product.'}
            </p>

            <div className="premium-modal-info-grid">
              <div className="info-item">
                <strong>Status:</strong>
                <span className={`status-value ${statusClass}`}>{statusLabel}</span>
              </div>
              <div className="info-item">
                <strong>Category:</strong>
                <span className="category-value">{product.category || 'Uncategorized'}</span>
              </div>
            </div>

            <div className="premium-modal-footer">
              <button className="premium-btn btn-primary-premium" onClick={handleNavigate}>
                <i className="ri-eye-line me-2"></i> View Details
              </button>

              {/* REPLACED: Make an Offer -> Edit Product */}
              <button
                className="premium-btn btn-primary-premium"
                onClick={() => {
                  // open ProductAdd in edit mode - use onEdit callback to let parent open the modal
                  if (typeof onEdit === 'function') onEdit(product);
                  onClose();
                }}
              >
                <i className="ri-pencil-line me-2"></i> Edit Product
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- USER PAGE COMPONENT ----------------
const Userpage = () => {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [productAddOpen, setProductAddOpen] = useState(false); // <-- open add/edit modal
  const [productToEdit, setProductToEdit] = useState(null); // <-- product being edited (or null)
  const navigate = useNavigate();

  // ==================== Fetch logged-in user's products from your API (localStorage-only) ====================
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setProductsLoading(true);

        // 1) Read localStorage.user only (you requested to remove /me and /my-products)
        let me = null;
        try {
          const raw = localStorage.getItem('user');
          if (raw) {
            // Try parse; if invalid or contains HTML, ignore it.
            try {
              const parsed = JSON.parse(raw);
              // Accept parsed only if it looks like a user object (has email or id)
              if (parsed && (parsed.email || parsed.user?.email || parsed.id || parsed.user?.id)) {
                me = parsed.user || parsed;
              } else {
                me = null;
              }
            } catch (parseErr) {
              // raw might be non-JSON (e.g. HTML) — ignore
              me = null;
            }
          }
        } catch (e) {
          console.warn('[Userpage] reading localStorage.user failed:', e);
          me = null;
        }

        // If no local user found -> hide products
        if (!me) {
          console.warn('[Userpage] No logged-in user found in localStorage. Hiding products.');
          if (mounted) {
            setProducts([]);
            setProductsLoading(false);
          }
          return;
        }

        const loggedId = me?.id || me?.user?.id || me?.data?.id || null;
        const loggedEmail = (me?.email || me?.user?.email || me?.data?.email || '')
          .toString()
          .trim()
          .toLowerCase();

        if (!loggedEmail && !loggedId) {
          console.warn('[Userpage] logged id/email not found in localStorage.user. Hiding products.');
          if (mounted) {
            setProducts([]);
            setProductsLoading(false);
          }
          return;
        }

        // 2) Fetch all products from server (you said /products exists)
        let productsResp = null;
        try {
          productsResp = await apiFetch('/products');
        } catch (e) {
          console.error('[Userpage] failed to fetch /products:', e);
          productsResp = null;
        }

        if (!mounted) return;

        // Normalize response shape into an array
        let items = [];
        if (Array.isArray(productsResp)) items = productsResp;
        else if (productsResp?.products && Array.isArray(productsResp.products)) items = productsResp.products;
        else if (productsResp?.data && Array.isArray(productsResp.data)) items = productsResp.data;
        else items = [];

        // 3) Filter by user id (preferred) OR email
        const final = (items || [])
          .filter((p) => {
            // ids
            const candidateIds = [p?.user_id, p?.owner_id, p?.user?.id, p?.owner?.id, p?.ownerId]
              .filter(Boolean)
              .map((x) => String(x));

            if (loggedId && candidateIds.includes(String(loggedId))) return true;

            // emails
            const candidateEmails = [
              p?.email,
              p?.owner_email,
              p?.user_email,
              p?.user?.email,
              p?.owner?.email,
              p?.owner?.contact?.email
            ]
              .filter(Boolean)
              .map((s) => String(s).trim().toLowerCase());

            if (loggedEmail && candidateEmails.includes(loggedEmail)) return true;

            return false;
          })
          .map((p) => {
            const imageStr = p.image && typeof p.image === 'string' ? p.image.trim() : '';
            // note: getImageUrl will return absolute site-rooted URL or null
            return { ...p, image: imageStr ? getImageUrl(imageStr) : getImageUrl(p) };
          });

        // Normalize timestamps and sort newest-first
        const normalizedFinal = (final || [])
          .map((p) => ensureTimestamp(p))
          .sort(sortNewestFirst);

        if (mounted) setProducts(normalizedFinal);
      } catch (err) {
        console.error('Failed to load products (localStorage-only flow):', err);
        setProducts([]);
      } finally {
        if (mounted) setProductsLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  // ---------- LISTEN FOR product-updated / product-added EVENTS ----------
  // NOTE: listeners intentionally do NOT mutate the visible list.
  // New or updated products will only show after a manual page reload.
  useEffect(() => {
    const onProdUpdated = (_e) => {
      // Intentionally empty: do not auto-update UI
    };

    const onProdAdded = (_e) => {
      // Intentionally empty: do not auto-update UI
    };

    window.addEventListener('product-updated', onProdUpdated);
    window.addEventListener('product-added', onProdAdded);

    return () => {
      window.removeEventListener('product-updated', onProdUpdated);
      window.removeEventListener('product-added', onProdAdded);
    };
  }, []);

  // ==================== Product Handlers ====================
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setPremiumModalOpen(true);
  };

  // Called by PremiumModal to open the ProductAdd modal in edit mode
  const openEditModal = (product) => {
    setProductToEdit(product || null);
    setProductAddOpen(true);
  };

  const handleRequestClick = (product) => {
    setSelectedProduct(product);
    setRequestModalOpen(true);
  };

  // When ProductAdd calls back with added/updated product, DO NOT update local list.
  // Just close the modal — UI will only reflect changes after a real page refresh.
  const handleProductAdded = (newOrUpdatedProduct) => {
    // close modal after receive
    setProductAddOpen(false);
    setProductToEdit(null);

    // Intentionally do NOT mutate `products` state.
    try {
      window.dispatchEvent(new CustomEvent('product-saved', { detail: newOrUpdatedProduct }));
    } catch (e) {
      // ignore
    }
  };

  const handleRequestSubmit = async (requesterEmail, productId) => {
    try {
      const body = {
        requester_email: requesterEmail,
        product_id: productId
      };
      const res = await apiFetch('/swap-requests', {
        method: 'POST',
        body: JSON.stringify(body)
      });

      const msg = res?.message || 'Request sent successfully!';
      alert('✅ ' + msg);
    } catch (error) {
      console.error('Error sending swap request:', error);
      const message = error?.body?.message || error?.message || 'Failed to send request';
      alert('❌ ' + message);
      throw error;
    }
  };

  const togglePremiumModal = () => setPremiumModalOpen((s) => !s);
  const toggleRequestModal = () => setRequestModalOpen((s) => !s);

  const handleNavClick = (category) => {
    setActiveCategory(category);
  };

  const filteredProducts = products.filter(
    (product) =>
      activeCategory === 'All' ||
      activeCategory.toLowerCase() === (product.category || '').toLowerCase()
  );

  return (
    <>


      {/* HERO */}
      <section style={styles.hero} aria-label="Contact hero">
        <h1 style={styles.heroTitle}>My Products</h1>
        <p
          style={{
            fontSize: "18px",
            color: "#000000ff",
            marginTop: "15px",
            fontWeight: 500,
            lineHeight: "1.6",
            textAlign: "center"
          }}
        >
          Manage your products and view incoming requests.
        </p>

        
      </section>



      <section className="section bg-light" id="products" style={styles.productsSection}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="text-center mb-5">

<Nav
  variant="pills"
  className="nav-pills filter-btns justify-content-center"
  role="tablist"
>
  {/* ALL PRODUCTS TAB */}
  <Nav.Item>
    <Nav.Link
      as="button"
      onClick={() => handleNavClick("All")}
      className={activeCategory === "All" ? "active" : ""}
      style={{
        padding: "8px 10px",
        borderRadius: "20px",
        border: "none",
        fontSize: "15px",
        cursor: "pointer",
        transition: "all 0.3s ease",

        // ✅ FONT WEIGHT CONTROL
        fontWeight: activeCategory === "All" ? 700 : 600,

        // ✅ COLOR + BACKGROUND
        color: activeCategory === "All" ? "#fff" : "#702963",
        background: activeCategory === "All" ? "#702963" : "transparent",
      }}
    >
      All Products
    </Nav.Link>
  </Nav.Item>

  {/* CATEGORY TABS */}
  {[
    "Toys & Games",
    "Sporting Goods",
    "Accessories",
    "Clothes",
    "Shoes",
    "Books",
    "Bags",
  ].map((cat) => (
    <Nav.Item key={cat}>
      <Nav.Link
        as="button"
        onClick={() => handleNavClick(cat)}
        className={activeCategory === cat ? "active" : ""}
        style={{
          padding: "8px 18px",
          borderRadius: "20px",
          border: "none",
          fontSize: "15px",
          cursor: "pointer",
          transition: "all 0.3s ease",

          // ✅ FONT WEIGHT CONTROL
          fontWeight: activeCategory === cat ? 700 : 600,

          // ✅ COLOR + BACKGROUND
          color: activeCategory === cat ? "#fff" : "#702963",
          background: activeCategory === cat ? "#FF7F50 " : "transparent",
        }}
      >
        {cat}
      </Nav.Link>
    </Nav.Item>
  ))}
</Nav>


              </div>
            </Col>
          </Row>

          <Row key={activeCategory}>
            {productsLoading ? (
              <Col className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading products...</p>
              </Col>
            ) : filteredProducts.length === 0 ? (
              <Col className="text-center">
                <p className="text-muted">
                  No products found . You are Not Added Any Products .
                </p>
              </Col>
            ) : (
              filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id || product._id || index}
                  product={product}
                  index={index}
                  onProductClick={handleProductClick}
                />
              ))
            )}
          </Row>
        </Container>
      </section>

      {/* Premium Modal */}
      <PremiumModal
        isOpen={premiumModalOpen}
        onClose={togglePremiumModal}
        product={selectedProduct}
        onEdit={openEditModal}
      />

      {/* Request Modal - Separated Component */}
      <RequestModal
        show={requestModalOpen}
        onClose={toggleRequestModal}
        product={selectedProduct}
        onRequestSubmit={handleRequestSubmit}
      />

      {/* ProductAdd reused as Edit modal */}
      <ProductAdd
        show={productAddOpen}
        handleClose={() => {
          setProductAddOpen(false);
          setProductToEdit(null);
        }}
        onProductAdded={handleProductAdded}
        initialProduct={productToEdit}
        editMode={Boolean(productToEdit)}
      />

      {/* Embedded Styles */}
      <style>{`
        .product-item-animate { 
          animation: fadeInUp 0.5s ease-out forwards; 
          opacity: 0; 
        }
        
        @keyframes fadeInUp { 
          from { 
            opacity:0; 
            transform:translateY(30px);
          } 
          to { 
            opacity:1; 
            transform:translateY(0); 
          } 
        }
        
        .premium-modal-overlay { 
          position:fixed; 
          top:0; 
          left:0; 
          right:0; 
          bottom:0; 
          background:rgba(248,249,250,0.7); 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          z-index:1050; 
          backdrop-filter:blur(8px); 
          -webkit-backdrop-filter:blur(8px); 
          animation: modalFadeIn 0.4s ease-out; 
        }
        
        .premium-modal-content { 
          background:#fff; 
          color:#343a40; 
          padding:2.5rem 3rem; 
          border-radius:16px; 
          border:1px solid #e9ecef; 
          box-shadow:0 25px 50px -12px rgba(0,0,0,0.15); 
          width:90%; 
          max-width:950px; 
          position:relative; 
          animation: modalSlideUp 0.5s cubic-bezier(0.16,1,0.3,1);
        }
        
        .premium-modal-close-btn { 
          position:absolute; 
          top:15px; 
          right:15px; 
          background:#f1f1f1; 
          border:none; 
          width:36px; 
          height:36px; 
          border-radius:50%; 
          font-size:22px; 
          line-height:36px; 
          text-align:center; 
          cursor:pointer; 
          color:#555; 
          transition: all 0.2s ease; 
        }
        
        .premium-modal-close-btn:hover { 
          background:#e0e0e0; 
          color:#000; 
        }
        
        .premium-modal-body-grid { 
          display:grid; 
          grid-template-columns:0.8fr 1.2fr; 
          gap:3rem; 
          align-items:center; 
        }
        
        .premium-modal-img-container { 
          width:100%; 
          border-radius:12px; 
          overflow:hidden; 
          position: relative;
          background: #f8f9fa;
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .premium-modal-img { 
          width:100%; 
          height:300px; 
          object-fit:cover; 
          display:block; 
          transition: opacity 0.3s ease;
        }
        
        .premium-modal-details { 
          display:flex; 
          flex-direction:column; 
          height:100%; 
        }
        
        .premium-modal-category { 
          text-transform:uppercase; 
          color:#0056b3; 
          font-size:0.85rem; 
          font-weight:700; 
          letter-spacing:1px; 
          margin-bottom:0.5rem; 
        }
        
        .premium-modal-title { 
          font-size:2rem; 
          font-weight:600; 
          color:#212529; 
          marginBottom:1rem; 
          line-height:1.2; 
        }
        
        .premium-modal-description { 
          color:#6c757d; 
          font-size:0.95rem; 
          line-height:1.7; 
          margin-bottom:1.5rem; 
          flex-grow: 1;
        }

        .premium-modal-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item strong {
          font-size: 0.85rem;
          color: #6c757d;
        }

        .status-value {
          font-size: 1rem;
          font-weight: 700;
        }

        .status-value.approved {
          color: #198754;
        }

        .status-value.pending {
          color: #ffc107;
        }

        .status-value.rejected {
          color: #dc3545;
        }

        .status-value.unknown {
          color: #6c757d;
        }

        .category-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: #0056b3;
        }
        
        .premium-modal-footer { 
          display:flex; 
          gap:1rem; 
          padding-top:1.5rem; 
          margin-top:1rem; 
          border-top:1px solid #e9ecef; 
        }
        
        .premium-btn { 
          border:none; 
          padding:0.9rem 1.5rem; 
          border-radius:8px; 
          font-size:1rem; 
          font-weight:600; 
          cursor:pointer; 
          transition: all 0.2s ease; 
          position:relative; 
          overflow:hidden; 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          flex: 1;
        }
        
        .premium-btn:not(:disabled):hover { 
          transform: translateY(-3px); 
          box-shadow: 0 8px 20px rgba(0,0,0,0.1); 
        }
        
        .premium-btn::before { 
          content: ''; 
          position:absolute; 
          top:0; 
          left:-100%; 
          width:100%; 
          height:100%; 
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.4), transparent); 
          transition:left 0.6s ease; 
        }
        
        .premium-btn:hover::before { 
          left:100%; 
        }
        
        .premium-btn i { 
          vertical-align: middle; 
          line-height: 1; 
        }
        
        .premium-btn:disabled { 
          opacity:0.7; 
          cursor:not-allowed; 
        }
        
        .btn-primary-premium { 
          background: linear-gradient(45deg,#0d6efd,#0056b3); 
          color:#fff; 
        }

        .btn-warning-premium { 
          background: linear-gradient(45deg,#ffc107,#fd7e14); 
          color:#fff; 
        }
        
        @keyframes modalFadeIn { 
          from { opacity:0; } 
          to { opacity:1; } 
        }
        
        @keyframes modalSlideUp { 
          from { 
            opacity:0; 
            transform: translateY(50px) scale(0.95); 
          } 
          to { 
            opacity:1; 
            transform: translateY(0) scale(1); 
          } 
        }
        
        @media (max-width:768px) {
          .premium-modal-content { padding:1.5rem; }
          .premium-modal-body-grid { grid-template-columns:1fr; gap:1.5rem; }
          .premium-modal-title { font-size:1.7rem; }
          .premium-modal-info-grid { grid-template-columns: 1fr; }
          .premium-modal-footer { flex-direction: column; }
          .premium-btn { width:100%; }
        }

        .explore-place-bid-img {
          position: relative;
          overflow: hidden;
          height: 200px;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bg-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.3);
          opacity: 0;
          transition: all 0.3s ease;
        }

        .place-bid-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0.8);
          opacity: 0;
          transition: all 0.3s ease;
        }

        .explore-place-bid-img:hover .bg-overlay {
          opacity: 1;
        }

        .explore-place-bid-img:hover .place-bid-btn {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }

        .nav-pills .nav-link.active {
          background-color: #FF7F50;
          color: white;
        }
      `}</style>
    </>
  );
};

// ==================== Styles ====================
const styles = {
  productsSection: {
      maxWidth: "2000px",   // 👈 YAHAN CONTROL
  margin: "0 auto",     // center me lane ke liye

    marginTop: '40px',
    padding: '20px 0'
  },
  productCard: {
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: 'none',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  productImage: {
    height: '200px',
    objectFit: 'cover',
    width: '100%',
    transition: 'opacity 0.3s ease'
  },
  navLink: {
    cursor: 'pointer',
    margin: '0 5px',
    borderRadius: '20px',
    padding: '8px 12px'
  },
  imageLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#6c757d',
    zIndex: 1
  },
  modalImageLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#6c757d',
    zIndex: 1
  },

hero: {
  background: "#ffffffff",
  padding: "20px 0",
  textAlign: "center",
  maxWidth: "100%",

},

    heroTitle: {
      color: "#702963",
      fontFamily: "'League Spartan', sans-serif",
      fontSize: 48,
      margin: 0,
      fontWeight: 700,
    }

};

export default Userpage;
